/**
 * BeatNode Component
 *
 * A custom React Flow node representing a single story beat.
 * Displays:
 * - Title
 * - Thumbnail of the image (if set)
 * - Duration
 * - Script preview
 *
 * This component is registered with React Flow as a custom node type.
 * React Flow passes the node's data and state as props.
 *
 * DRAG AND DROP:
 * ==============
 * Images can be dropped directly onto a beat node.
 * The drop handler copies the image to the project folder
 * and updates the node's imagePath.
 */

import { memo, useCallback, CSSProperties, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { BeatNodeData } from '@/types';
import { useProjectStore } from '@/stores/projectStore';
import { useFocusedNodeId } from '@/stores/playbackStore';
import { getImageUrl as getBrowserImageUrl } from '@/utils/browserAPI';
import { secondsToFrameNotation } from '@/utils/frameUtils';
import { getDisplayDimensions, resizeImageForImport } from '@/utils/imageProcessing';
import { blobManager } from '@/utils/blobManager';

/**
 * BeatNode Component
 *
 * `memo` is a React optimization that prevents re-renders when props haven't changed.
 * Since React Flow can have many nodes, this is important for performance.
 *
 * We use NodeProps without generics for simpler React Flow v12 compatibility,
 * then cast data to our known BeatNodeData type inside the component.
 */
const BeatNode = memo(function BeatNode({ id, data, selected }: NodeProps) {
  // Cast data to our known type (we know what data shape our nodes have)
  const nodeData = data as BeatNodeData;

  // Store actions
  const updateNode = useProjectStore((state) => state.updateNode);
  const projectPath = useProjectStore((state) => state.projectPath);
  const selectNode = useProjectStore((state) => state.selectNode);
  const saveHistory = useProjectStore((state) => state.saveHistory);
  const fps = useProjectStore((state) => state.fps);
  const aspectRatio = useProjectStore((state) => state.aspectRatio);
  const nodes = useProjectStore((state) => state.nodes);
  const focusedNodeId = useFocusedNodeId();

  // Check if this node is the focused node (at playhead position)
  const isFocused = id === focusedNodeId;

  // Track image loading state
  const [imageError, setImageError] = useState(false);

  // ---------------------------------------------------------------------------
  // DRAG AND DROP HANDLER
  // ---------------------------------------------------------------------------

  /**
   * Handle image drop onto this node
   *
   * When a user drags an image file onto the node:
   * 1. Prevent default browser behavior
   * 2. Get the file from the drop event
   * 3. Copy it to the project's images folder (or create blob URL in browser)
   * 4. Update the node's imagePath
   */
  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Get dropped files
      const files = Array.from(event.dataTransfer.files);
      const imageFile = files.find((f) =>
        f.type.startsWith('image/') ||
        /\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name)
      );

      if (!imageFile) return;

      try {
        // Resize image to optimize storage and performance
        const resizedBlob = await resizeImageForImport(imageFile);
        
        // Check if we have Electron API with file paths
        const filePath = (imageFile as File & { path?: string }).path;

        if (filePath && window.electronAPI && projectPath) {
          // Electron mode: Use resized blob as URL for copyImage
          const resizedUrl = URL.createObjectURL(resizedBlob);
          const projectDir = projectPath.replace(/[/\\][^/\\]+$/, '');
          const result = await window.electronAPI.copyImage(resizedUrl, projectDir);
          URL.revokeObjectURL(resizedUrl);

          if (result.success && result.relativePath) {
            updateNode(id, { imagePath: result.relativePath });
          }
        } else {
          // Browser mode: create blob URL from resized image
          const blobUrl = blobManager.create(resizedBlob, `beat-node-image:${id}`);
          const result = await window.electronAPI?.copyImage(blobUrl, '');

          if (result?.success && result.relativePath) {
            updateNode(id, { imagePath: result.relativePath });
          }
        }
      } catch (error) {
        console.error('Failed to process image:', error);
      }
    },
    [id, updateNode, projectPath]
  );

  /**
   * Prevent default for drag over (required for drop to work)
   */
  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  /**
   * Handle mousedown to ensure immediate selection
   */
  const handleMouseDown = useCallback(() => {
    selectNode(id);
  }, [id, selectNode]);

  /**
   * Handle double-click to move node to shelf
   */
  const handleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      // Find the current node to get its position
      const currentNode = nodes.find((n) => n.id === id);
      if (!currentNode) return;

      // Save history before moving to shelf (for undo support)
      saveHistory();

      // Store current position before moving to shelf
      updateNode(id, {
        onShelf: true,
        shelfStoredPosition: { x: currentNode.position.x, y: currentNode.position.y }
      });
    },
    [id, nodes, updateNode, saveHistory]
  );

  // ---------------------------------------------------------------------------
  // GET IMAGE URL
  // ---------------------------------------------------------------------------

  /**
   * Resolve the image path to a full URL for display
   * Works with both Electron (file://) and browser (blob:) URLs
   */
  const getImageUrl = useCallback(() => {
    if (!nodeData.imagePath) return null;

    const imagePath = nodeData.imagePath;

    // Try browser API first (handles blob: URLs)
    const browserUrl = getBrowserImageUrl(imagePath);
    if (browserUrl) return browserUrl;

    // Fall back to Electron file:// URL
    if (!projectPath) return null;
    const projectDir = projectPath.replace(/[/\\][^/\\]+$/, '');
    const absolutePath = `${projectDir}/${imagePath}`.replace(/\\/g, '/');
    return `file://${absolutePath}`;
  }, [nodeData.imagePath, projectPath]);

  const imageUrl = getImageUrl();

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  // Use custom color if set, otherwise use theme default
  const nodeBackgroundColor = nodeData.color || 'var(--nb-node-bg)';

  const nodeStyles: CSSProperties = {
    width: 180,
    backgroundColor: nodeBackgroundColor,
    border: `2px solid ${selected ? 'var(--nb-node-selected)' : 'var(--nb-node-border)'}`,
    borderRadius: 'var(--nb-radius-md)',
    overflow: 'hidden',
    boxShadow: selected ? 'var(--nb-shadow-md)' : 'var(--nb-shadow-sm)',
    transition: 'border-color var(--nb-transition-fast), box-shadow var(--nb-transition-fast)',
    position: 'relative',
  };

  const focusIndicatorStyles: CSSProperties = {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: '#22c55e', // Green color
    border: '2px solid white',
    zIndex: 10,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  };

  const headerStyles: CSSProperties = {
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-tertiary)',
    borderBottom: '1px solid var(--nb-border-primary)',
    fontSize: 'var(--nb-font-size-xs)',
    fontWeight: 600,
    color: 'var(--nb-text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  // Calculate image container dimensions based on aspect ratio
  const nodeWidth = 180;
  const imageContainerMaxHeight = 120;
  const imageDimensions = getDisplayDimensions(nodeWidth, imageContainerMaxHeight, aspectRatio);

  const imageContainerStyles: CSSProperties = {
    height: imageDimensions.height,
    width: nodeWidth,
    backgroundColor: 'var(--nb-bg-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: '1px solid var(--nb-border-primary)',
    overflow: 'hidden',
  };

  const imageStyles: CSSProperties = {
    width: imageDimensions.width,
    height: imageDimensions.height,
    objectFit: 'contain',
  };

  const placeholderStyles: CSSProperties = {
    color: 'var(--nb-text-muted)',
    fontSize: 'var(--nb-font-size-xs)',
    textAlign: 'center',
    padding: 'var(--nb-space-sm)',
  };

  const contentStyles: CSSProperties = {
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
  };

  const scriptPreviewStyles: CSSProperties = {
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-secondary)',
    lineHeight: 1.3,
    maxHeight: 40,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  };

  const durationStyles: CSSProperties = {
    marginTop: 'var(--nb-space-xs)',
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-muted)',
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <div
      style={nodeStyles}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onMouseDownCapture={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {/* Green focus indicator when this beat is at the playhead */}
      {isFocused && <div style={focusIndicatorStyles} title="Current playhead position" />}

      {/* Connection handles (for potential future use) */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ opacity: 0 }}
      />

      {/* Node header with title */}
      <div style={headerStyles}>
        {nodeData.title || 'Untitled Beat'}
      </div>

      {/* Image area */}
      <div style={imageContainerStyles}>
        {imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={nodeData.title}
            style={imageStyles}
            draggable={false}
            onError={() => setImageError(true)}
            onLoad={() => setImageError(false)}
          />
        ) : imageError ? (
          <div style={{ ...placeholderStyles, color: 'var(--nb-accent-error)' }}>
            Image load failed
          </div>
        ) : (
          <div style={placeholderStyles}>
            Drop image here
          </div>
        )}
      </div>

      {/* Content area */}
      <div style={contentStyles}>
        {/* Script preview */}
        {nodeData.script ? (
          <div style={scriptPreviewStyles}>
            {nodeData.script}
          </div>
        ) : (
          <div style={{ ...scriptPreviewStyles, fontStyle: 'italic' }}>
            No script
          </div>
        )}

        {/* Duration */}
        <div style={durationStyles}>
          {secondsToFrameNotation(nodeData.duration, fps)}
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{ opacity: 0 }}
      />
    </div>
  );
});

export default BeatNode;
